import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function Support() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" component="h1" gutterBottom fontWeight={600}>
        Blackmount Support
      </Typography>

      {/* Quick Start Guide */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: 'background.paper' }}>
        <Typography variant="h5" gutterBottom fontWeight={500}>
          Quick Start Guide
        </Typography>

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Desktop (Mac, PC, Linux)
        </Typography>
        <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li>
            Create an account at{' '}
            <Link to="/" style={{ color: '#667eea', textDecoration: 'none' }}>
              app.blackmount.ai
            </Link>
          </li>
          <li>
            Install the browser extension from the{' '}
            <a href="https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
              Chrome Web Store
            </a>
            {' '}(Chrome, Brave, Vivaldi, Opera),{' '}
            <a href="https://microsoftedge.microsoft.com/addons/detail/blackmount-%E2%80%93-save-resto/joehjcmaaehannjlhaaiikflfhcamdoh" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
              Microsoft Edge Add-ons
            </a>
            , or{' '}
            <a href="https://addons.mozilla.org/en-US/firefox/addon/blackmount/" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
              Firefox Add-ons
            </a>
          </li>
          <li>
            Click <strong>Sign In</strong> in the extension popup — you'll be redirected to sign in with the same account you used on the web app
          </li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          iPhone & iPad
        </Typography>
        <Box component="ol" sx={{ pl: 3, '& li': { mb: 1 } }}>
          <li>Download the <strong>Blackmount</strong> app from the App Store</li>
          <li>Sign in with Apple or Google</li>
          <li>Enable the Safari extension: <strong>Settings → Safari → Extensions → Blackmount</strong></li>
          <li>Your sessions, bookmarks, and AI chats sync automatically across all devices</li>
        </Box>
      </Paper>

      {/* Features */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight={500}>
          Features
        </Typography>

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Save Sessions
        </Typography>
        <Typography variant="body1" paragraph>
          Capture all open tabs in one click:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>Click extension icon</li>
          <li>Click "Save Session"</li>
          <li>Name your session (optional)</li>
        </Box>
        <Typography variant="body2" color="text.secondary">
          <strong>Keyboard Shortcut:</strong> Ctrl+Shift+S (Cmd+Shift+S on Mac)
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Restore Sessions
        </Typography>
        <Typography variant="body1" paragraph>
          Reopen saved tabs instantly:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>Click extension icon</li>
          <li>Search for session by name</li>
          <li>Click "Restore" to open in new window</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Quick Bookmarks
        </Typography>
        <Typography variant="body1" paragraph>
          Save individual tabs to projects:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>Click extension icon</li>
          <li>Click "Quick Bookmark"</li>
          <li>Select destination project</li>
        </Box>
        <Typography variant="body2" color="text.secondary">
          <strong>Keyboard Shortcut:</strong> Ctrl+Shift+B (Cmd+Shift+B on Mac)
        </Typography>
      </Box>

      {/* iOS App */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight={500}>
          iOS App
        </Typography>

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Save Safari Tabs
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>Open Safari on your iPhone or iPad</li>
          <li>Tap the puzzle piece icon (Extensions) in the address bar</li>
          <li>Tap Blackmount → "Save All Tabs"</li>
          <li>Your session syncs to all your devices</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Voice Notes
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>Open Blackmount → Voice tab</li>
          <li>Tap the record button</li>
          <li>When you stop, your voice is transcribed automatically on your iPhone</li>
          <li>Tap "Generate Summary" for an AI-powered summary (requires one-time consent)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          Share from Any App
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>In any app (Safari, Twitter, Reddit, etc.), tap Share</li>
          <li>Select Blackmount from the share sheet</li>
          <li>Choose a project to save into</li>
          <li>The link is saved and synced across devices</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
          AI Chats
        </Typography>
        <Typography variant="body1" paragraph>
          The AI Chats tab shows conversations captured from ChatGPT, Claude, and Gemini
          by your desktop browser extension. It is read-only on iOS — tap "Open in ChatGPT"
          to continue a conversation in the original app.
        </Typography>
      </Box>

      {/* FAQ */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight={500}>
          Frequently Asked Questions
        </Typography>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>How do I connect the browser extension?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Click the <strong>Sign In</strong> button in the extension popup. You'll be redirected to
              app.blackmount.ai to sign in with your Google or email account. Once signed in, the
              extension connects automatically.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>How many tabs can I save in a session?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              No limit! Save as many tabs as you need in a single session.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Can I use Blackmount on multiple computers?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Yes! Your sessions sync automatically across all devices where you're signed in with the same account.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Where is my data stored?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Your data is encrypted and stored securely on our servers. We only collect data when you explicitly
              click "Save Session" or "Save Bookmark" - no background tracking.
              See our <Link to="/privacy-policy" style={{ color: '#667eea' }}>Privacy Policy</Link> for details.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Extension won't connect - what should I do?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography component="div">
              <Box component="ol" sx={{ pl: 2, mb: 0 }}>
                <li>Check your internet connection</li>
                <li>Try signing out and signing back in from the extension popup</li>
                <li>Make sure you're using the same account as the web app</li>
                <li>Contact support if the issue persists</li>
              </Box>
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>How do I enable the Safari extension on iPhone?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Go to <strong>Settings → Safari → Extensions → Blackmount</strong> and toggle it ON.
              Allow access to "All Websites" when prompted.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Does voice transcription require internet?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              No. Transcription runs entirely on your iPhone using Apple's speech recognition.
              Your audio never leaves your device.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>What is Groq and why does the app mention it?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Groq, Inc. is the AI provider that powers optional text summaries. When you tap
              "Generate Summary," the app asks for your permission before sending any text to Groq.
              You can disable this anytime in <strong>Settings → AI & Privacy</strong>.
              Voice audio is never sent to Groq.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Why can't I see my AI chats on iOS?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              AI chats are captured by the desktop browser extension. Install the Blackmount extension
              on Chrome, Edge, or Firefox, visit ChatGPT/Claude/Gemini, and the extension will save
              your conversations. They'll appear on iOS after syncing.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={500}>Do my sessions expire?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              No, saved sessions are kept indefinitely unless you delete them. You can organize and manage
              all your sessions from the web app.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Browser Compatibility */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom fontWeight={500}>
          Supported Browsers
        </Typography>
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.5 } }}>
          <li>Chrome 90+ ✅</li>
          <li>Firefox 121+ ✅ (via <a href="https://addons.mozilla.org/en-US/firefox/addon/blackmount/" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>Firefox Add-ons</a>)</li>
          <li>Microsoft Edge 90+ ✅ (via <a href="https://microsoftedge.microsoft.com/addons/detail/blackmount-%E2%80%93-save-resto/joehjcmaaehannjlhaaiikflfhcamdoh" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>Microsoft Edge Add-ons</a>)</li>
          <li>Brave 1.30+ ✅ (via Chrome Web Store)</li>
          <li>Vivaldi 4.0+ ✅ (via Chrome Web Store)</li>
          <li>Opera 76+ ✅ (via Chrome Web Store)</li>
          <li>Safari on iOS/iPadOS ✅ (via built-in Safari extension, included with iOS app)</li>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          💡 <strong>Chromium-based browsers</strong> (Brave, Vivaldi, Opera) can install the extension directly from the Chrome Web Store.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          🌐 <strong>Microsoft Edge</strong> now has its own store listing at Microsoft Edge Add-ons.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          🦊 <strong>Firefox users</strong> can install from Firefox Add-ons store.
        </Typography>
      </Paper>

      {/* Contact Support */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Typography variant="h5" gutterBottom fontWeight={500}>
          Contact Support
        </Typography>
        <Typography variant="body1" paragraph>
          Email:{' '}
          <a href="mailto:support@blackmount.ai" style={{ color: '#667eea', textDecoration: 'none' }}>
            support@blackmount.ai
          </a>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Response Time:</strong> Within 24-48 hours
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          When contacting support, please include your browser name/version, extension version,
          and a brief description of the issue.
        </Typography>
      </Paper>

      {/* Footer Links */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <Link to="/privacy-policy" style={{ color: '#667eea', textDecoration: 'none', marginRight: 16 }}>
            Privacy Policy
          </Link>
          <Link to="/terms" style={{ color: '#667eea', textDecoration: 'none' }}>
            Terms of Service
          </Link>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last updated: April 2026
        </Typography>
      </Box>
    </Container>
  );
}
